import { type ReactNode } from 'react';
import { CTAButton } from '../landing_page/CTAButton';
import { assertArticleCtaPosition, type ArticleCtaPosition } from './ctaPosition';
import styles from './index.module.scss';
import { useCtaViewTracking, type CtaType } from './useCtaViewTracking';

type Theme = 'monteChat' | 'diagnosis' | 'trialClass' | 'kidsClass';
type LayoutVariant = 'overlayRightImage' | 'sideBySideNoOverlay';
type ImageCropVariant = 'legacy' | 'centered';

type Props = {
  heading: ReactNode;
  subtext: string;
  ctaLabel: string;
  href: string;
  dataTrackingId?: string;
  imageSrc: string;
  imageAlt: string;
  onCtaClick?: () => void;
  theme?: Theme;
  layoutVariant?: LayoutVariant;
  imageCropVariant?: ImageCropVariant;
  // 指定時のみ表示イベント `cta_view` を送る（クリックイベントを持つ CTA と同じ種別値）
  ctaType?: CtaType;
  ctaPosition?: ArticleCtaPosition;
};

export const BlogCtaCard = ({
  heading,
  subtext,
  ctaLabel,
  href,
  dataTrackingId,
  imageSrc,
  imageAlt,
  onCtaClick,
  theme = 'monteChat',
  layoutVariant = 'overlayRightImage',
  imageCropVariant = 'legacy',
  ctaType,
  ctaPosition,
}: Props) => {
  assertArticleCtaPosition(ctaPosition);
  const viewTrackingRef = useCtaViewTracking({ ctaType, ctaPosition });
  const themeClassMap: Record<Theme, string> = {
    monteChat: styles.monteChat,
    diagnosis: styles.diagnosis,
    trialClass: styles.trialClass,
    kidsClass: styles['kids-class'],
  };
  const themeClass = themeClassMap[theme];
  const layoutClass = layoutVariant === 'sideBySideNoOverlay' ? styles.sideBySideNoOverlay : styles.overlayRightImage;
  const imageCropClass = imageCropVariant === 'centered' ? styles.imageCentered : styles.imageLegacy;

  return (
    <div className={styles.base}>
      <div ref={viewTrackingRef} className={`${styles.card} ${themeClass} ${layoutClass}`} data-testid="blog-cta-card">
        <div className={styles.content}>
          <div className={styles['text-content']}>
            <div className={styles.heading}>{heading}</div>
            <p className={styles.subtext}>{subtext}</p>
          </div>
          <div className={styles['cta-wrapper']}>
            <CTAButton variant="primary" href={href} dataTrackingId={dataTrackingId} onClick={onCtaClick}>
              {ctaLabel}
            </CTAButton>
          </div>
        </div>
        <div className={styles['image-wrapper']}>
          <img src={imageSrc} alt={imageAlt} className={`${styles.image} ${imageCropClass}`} />
        </div>
      </div>
    </div>
  );
};
